import { AxiosError } from 'axios';

import { call, put, select, takeLeading } from 'redux-saga/effects';

import apiProjects from 'api/projects';

import { finishLoading, startLoading } from '../loading/actions';

import {
  FETCH_CONTRACT_TYPES,
  FETCH_WORK_TYPES,
  FETCH_BUILDING_TYPES,
  FETCH_COLLATERAL_TYPES,
  FETCH_EMPLOYEE_INFORMATION_TYPES,
  FETCH_WAY_OF_RECEIVING_TYPES,
  FETCH_TAX_RATE_TYPES,
  FETCH_TAB_TYPES,
  FETCH_HINDRANCE_TYPES,
  FETCH_PLACE_STATUS_TYPES,
} from './types';

/**
 * The fixed lists of the projects are read once: a page asking for them again uses the
 * list already read, so the page is not blocked by the loading spinner on every visit.
 * The spinner is cleared also when reading fails.
 */
function* fetchTypes(
  type: { PENDING: string; SUCCESS: string; FAILURE: string },
  variableName: string,
  apiEndpoint: () => Promise<unknown>
) {
  const currentValue: unknown[] = yield select(
    (state) => state.projects[variableName]
  );
  if (Array.isArray(currentValue) && currentValue.length > 0) {
    return;
  }

  try {
    yield put(startLoading(type.PENDING));
    const types = yield call(apiEndpoint);
    yield put({ type: type.SUCCESS, [variableName]: types });
  } catch (error) {
    const axiosError = error as AxiosError;
    yield put({ type: type.FAILURE, message: axiosError.message });
  } finally {
    yield put(finishLoading(type.PENDING));
  }
}

function* fetchContractTypes() {
  yield fetchTypes(
    FETCH_CONTRACT_TYPES,
    'contractTypes',
    apiProjects.contractTypes.get
  );
}

function* fetchBuildingTypes() {
  yield fetchTypes(
    FETCH_BUILDING_TYPES,
    'buildingTypes',
    apiProjects.buildingTypes.get
  );
}

function* fetchWorkTypes() {
  yield fetchTypes(FETCH_WORK_TYPES, 'workTypes', apiProjects.workTypes.get);
}

function* fetchCollateralTypes() {
  yield fetchTypes(
    FETCH_COLLATERAL_TYPES,
    'collateralTypes',
    apiProjects.collateralTypes.get
  );
}

function* fetchEmployeeInformationTypes() {
  yield fetchTypes(
    FETCH_EMPLOYEE_INFORMATION_TYPES,
    'employeeInformationTypes',
    apiProjects.employeeInformationTypes.get
  );
}

function* fetchWayOfReceivingTypes() {
  yield fetchTypes(
    FETCH_WAY_OF_RECEIVING_TYPES,
    'wayOfReceivingTypes',
    apiProjects.wayOfReceivingTypes.get
  );
}

function* fetchTaxRateTypes() {
  yield fetchTypes(
    FETCH_TAX_RATE_TYPES,
    'taxRateTypes',
    apiProjects.taxRateTypes.get
  );
}

function* fetchTabTypes() {
  yield fetchTypes(FETCH_TAB_TYPES, 'tabTypes', apiProjects.tabTypes.get);
}

function* fetchHindranceTypes() {
  yield fetchTypes(
    FETCH_HINDRANCE_TYPES,
    'hindranceTypes',
    apiProjects.hindrances
  );
}

function* fetchStatusTypes() {
  yield fetchTypes(
    FETCH_PLACE_STATUS_TYPES,
    'placeStatusTypes',
    apiProjects.statusTypes.get
  );
}

function* saga(): Generator {
  yield takeLeading(FETCH_CONTRACT_TYPES.PENDING, fetchContractTypes);
  yield takeLeading(FETCH_BUILDING_TYPES.PENDING, fetchBuildingTypes);
  yield takeLeading(FETCH_WORK_TYPES.PENDING, fetchWorkTypes);
  yield takeLeading(FETCH_COLLATERAL_TYPES.PENDING, fetchCollateralTypes);
  yield takeLeading(
    FETCH_EMPLOYEE_INFORMATION_TYPES.PENDING,
    fetchEmployeeInformationTypes
  );
  yield takeLeading(
    FETCH_WAY_OF_RECEIVING_TYPES.PENDING,
    fetchWayOfReceivingTypes
  );
  yield takeLeading(FETCH_TAX_RATE_TYPES.PENDING, fetchTaxRateTypes);
  yield takeLeading(FETCH_TAB_TYPES.PENDING, fetchTabTypes);
  yield takeLeading(FETCH_HINDRANCE_TYPES.PENDING, fetchHindranceTypes);
  yield takeLeading(FETCH_PLACE_STATUS_TYPES.PENDING, fetchStatusTypes);
}

export default saga;
